import {
  useWSSnackbar,
  WSActions,
  WSAvatar,
  WSDropFileInput,
  WSElement,
  WSFlexBox,
  WSIcon,
  WSText
} from "@wingspanhq/fe-component-library";
import React, { useRef, useState } from "react";

import { ErrorAlert } from "@components/ErrorAlert";
import { useMutationUploadVaultFile } from "../../../../../v3/utils/useMutationUploadVaultFile";

/** Matches the "Max 100MBs per file" the flow's instructions step promises. */
const MAX_FILE_SIZE = 100 * 1000000;

interface UploadDocumentModalProps {
  onClose(): void;
  onSuccess(props: { fileId: string }): void;
  accept?: string | string[];
}

export const ModalUploadFile: React.FC<UploadDocumentModalProps> = ({
  onClose,
  onSuccess,
  accept = "image/*, .pdf"
}) => {
  const [fileToUpload, setFileToUpload] = useState<File>();
  const { openSnackbar } = useWSSnackbar();
  const uploadContainerElement = useRef<HTMLElement>(null);

  // The certificate lands in the file vault as a private, generic file: the
  // import endpoint requires a private vault file owned by the request account,
  // and `Document` is reserved for signable artifacts a SignatureRequest can
  // target, which a certificate of insurance is not.
  const [uploadFile, uploadFileMeta] = useMutationUploadVaultFile();

  const onUpload = async () => {
    if (fileToUpload) {
      await uploadFile(
        { file: fileToUpload },
        {
          onSuccess: vaultFile => {
            setFileToUpload(undefined);
            openSnackbar({
              message: "File uploaded successfully",
              type: "success"
            });
            onSuccess({ fileId: vaultFile.id! });
            onClose();
          }
        }
      );
    }
  };

  return (
    <WSElement>
      <WSElement ref={uploadContainerElement} mb="XL">
        <WSDropFileInput
          accept={accept}
          buttonText="Choose a file"
          maxSize={MAX_FILE_SIZE}
          name="insuranceDocument"
          onDropAccepted={(accepted: File[]) => {
            setFileToUpload(accepted[0]);
          }}
        />
        {fileToUpload && (
          <>
            <WSElement mb="M" p="S">
              <WSFlexBox.CenterY wrap="nowrap">
                <WSAvatar.Icon
                  color="green500"
                  colorBackground="green50"
                  icon="paperclip"
                  mr="M"
                />
                <WSText.ParagraphSm singleLine>
                  {fileToUpload.name}
                </WSText.ParagraphSm>
                <WSIcon
                  block
                  color="gray500"
                  mx="M"
                  name="exit"
                  size="XS"
                  onClick={() => {
                    setFileToUpload(undefined);
                  }}
                />
              </WSFlexBox.CenterY>
            </WSElement>
          </>
        )}
      </WSElement>

      <ErrorAlert
        context="UploadDocument"
        error={uploadFileMeta.error}
        mb="M"
      />
      <WSActions
        alignment="fill"
        buttons={[
          {
            kind: "Secondary",
            label: "Cancel",
            onClick: onClose,
            type: "button"
          },
          {
            loading: uploadFileMeta.isLoading,
            disabled: !fileToUpload,
            kind: "Primary",
            onClick: onUpload,
            type: "button",
            label: "Upload"
          }
        ]}
      />
    </WSElement>
  );
};
